import { FormLayout, Remark, useDownload } from "@careinfo/layout";
import { AccessControl } from "@careinfo/permission-required";
import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import moment from "moment";
import { ClipboardEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../constant/EndPoint";
import usePatchHook from "../../customHooks/usePatchHook";
import DownloadIcon from "../../Icons/DownloadIcon";
import { createRunsheetValidationForm } from "../../utils/validationSchemas";
import Card from "../Reusable/Card";
import ComboboxInput from "../Reusable/ComboboxInput";
import DataTable from "../Reusable/DataTable";
import Modal from "../Reusable/Modal";
import Spinner from "../Reusable/Spinner";
import TextField from "../Reusable/TextInput";
import { getManifestItemColumn } from "../TableColumns/ManifestTableColumns";
import ChangeStatus from "./ChangeStatus";
import NumberField from "../Reusable/NumberField";
import TabPanel from "../Reusable/Tab";
import { OrderList } from "../../pages/Consignment/ConsignmentList";
import MultipleSelectField from "../Reusable/MultipleSelect";

const EditVehicleManifest = (props: any) => {
  const {
    handleEdit,
    isLoading,
    data,
    id,
    handleCheckConsignments,
    handleAttachConsignment,
    handlePrint,
    gridData,
    isPrintLoading,
    handleSplitPrint,
    defaultValue,
    handleStatusChange,
    isRemoved,
    handleRemove,
    refetch,
  } = props;
  const { download } = useDownload();

  const [searchParams, setSearchParams] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });

  const { mutateAsync: setMarkAsDone, isLoading: isMarkAsDoneLoading } =
    usePatchHook({
      queryKey: [`manifest-${id}`, "manifest"],
      navigateURL: "",
    });

  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [statusData, setStatusData] = useState(null);
  const [statusList, setStatusList] = useState(null);
  const [showMarkAsDoneModal, setShowMarkAsDoneModal] = useState(false);
  const selectedOrders = searchParams.get("selectedOrders");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm<any>({
    resolver: yupResolver(createRunsheetValidationForm),
    defaultValues: async () => {
      return {
        driver: {
          name: data?.driver?.user,
          id: data?.driver?.id,
        },
        vehicle: {
          name: data?.vehicle?.license_number,
          id: data?.vehicle?.id,
        },
        from_branch: data?.from_branch?.name,
        destination_branch: {
          name: data?.destination_branch?.name,
          id: data?.destination_branch?.id,
        },
        allowed_cities: data?.allowed_cities
          ? data?.allowed_cities?.map((item: any) => ({
              label: item?.name,
              value: item?.id,
            }))
          : [],
        freight: data?.freight,
        balance: data?.balance,
        advance: data?.advance,
        note: data?.note,
      };
    },
  });

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter" && event.target.value?.length > 0) {
      if (!data?.is_active) {
        handleCheckConsignments(event.target.value, setError);
      } else if (statusData?.length > 1 && defaultValue?.length === 1) {
        handleAttachConsignment(
          event.target.value,
          defaultValue[0]?.id,
          setError,
        );
      } else if (statusData?.length === 1) {
        handleAttachConsignment(
          event.target.value,
          statusData[0]?.id,
          setError,
        );
      } else {
        toast.error("Please select one status first");
      }
      event.target.value = "";
    }
  };

  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const handleIndividualSelect = (value: string, order: string) => {
    let selectedItems = searchParams.get("selectedItems")?.split(",");
    let selectedOrders = searchParams.get("selectedOrders")?.split(",");
    if (
      !selectedItems ||
      !selectedItems === undefined ||
      !selectedOrders ||
      !selectedItems === undefined
    ) {
      searchParams.set("selectedItems", value);
      searchParams.set("selectedOrders", order);
      setSearchParams(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    let orderIndex = selectedOrders?.findIndex((item) => item === order);
    if (itemIndex > -1 || orderIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      selectedOrders.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      const newSelectedOrders = selectedOrders
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length && newSelectedOrders.length) {
        searchParams.set("selectedItems", newSelectedItems);
        searchParams.set("selectedOrders", newSelectedOrders);
      } else {
        searchParams.delete("selectedItems");
        searchParams.delete("selectedOrders");
      }
      searchParams.set("selectAllItems", "0");
      setSearchParams(searchParams);
    } else {
      selectedItems?.push(value);
      selectedOrders?.push(order);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      const newSelectedOrders = selectedOrders
        ?.map((item) => item)
        .join(",")
        .toString();
      searchParams.set("selectedItems", newSelectedItems);
      searchParams.set("selectedOrders", newSelectedOrders);
      setSearchParams(searchParams);
    }
  };

  const isSelected = (value: string, order: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    const selectedOrders = searchParams.get("selectedOrders")?.split(",");
    if (!selectedItems && !selectedOrders) {
      return false;
    }
    return (selectedItems.includes(value), selectedOrders.includes(order));
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get("selectAllItems");
    if (isSelected === "1") {
      searchParams.set("selectAllItems", "0");
      searchParams.delete("selectedItems");
      searchParams.delete("selectedOrders");
      setSearchParams(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      const selectedOrders = tableData
        ?.map((item: any) => item?.order?.id ?? item.id)
        .join(",")
        .toString();
      searchParams.set("selectAllItems", "1");
      searchParams.set("selectedItems", selectedItems);
      searchParams.set("selectedOrders", selectedOrders);
      setSearchParams(searchParams);
    }
  };
  const manifestItemColumn = getManifestItemColumn({
    checkedAllItems,
    handleSelectAll,
    isSelected,
    handleIndividualSelect,
    setSelectedOrder,
    setOpenReviewModal,
  });

  const preventCopyPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    toast.error("Copy and paste is not allowed here");
  };

  const handleOrderDetailNavigate = (row) => {
    console.log(row?.original?.id);
  };

  const handleDownloadExcel = async () => {
    try {
      await download(
        `logisticservice/api/order/download-excel/`,
        `manifest-order-${moment().format("MMMM-Do-YYYY-h-mm-ss")}.xlsx`,
        selectedOrders,
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAsDone = () => {
    setShowMarkAsDoneModal(true);
  };

  const handleMarkAsDoneModalClose = () => {
    setShowMarkAsDoneModal(false);
  };

  const toggleMarkAsDone = async () => {
    const url = `${APIS.VEHICLE_MANIFEST}${id}/`;
    let formData = {
      is_active: false,
    };
    try {
      await setMarkAsDone({ url, formData, setError });
      await refetch();
    } catch (err) {
      console.error(err);
    } finally {
      handleMarkAsDoneModalClose();
    }
  };

  useEffect(() => {
    const freight = Number(watch("freight")) || 0;
    const advance = Number(watch("advance")) || 0;
    setValue("balance", freight - advance);
  }, [watch("freight"), watch("advance")]);

  useEffect(() => {
    if (Array.isArray(gridData)) {
      setStatusList(gridData);
    }
  }, [gridData]);

  useEffect(() => {
    if (Array.isArray(statusList)) {
      const status = statusList?.filter((x: any) => x?.name === "Intermediate");
      setStatusData(status[0]?.status_id);
    }
  }, [statusList]);
  const selectedCities = watch("allowed_cities") || [];

  const cityIds = selectedCities.map((city: any) => city.value).join(",");
  console.log(cityIds, "selectedCities");

  return (
    <>
      <AccessControl
        children={
          <FormLayout
            handleSubmit={handleSubmit}
            handleFormSubmit={handleEdit}
            setError={setError}
            buttonLabel={"Update"}
            isLoading={isLoading}
            disabled={!data?.is_active}
          >
            <div className={tw`grid grid-cols-4 gap-x-4 mx-4`}>
              <ComboboxInput
                url={APIS.VEHICLE}
                queryKey={"vehicle"}
                register={register("vehicle")}
                errors={errors?.vehicle}
                name={"vehicle"}
                label={"Vehicle Number"}
                props={{
                  is_active: true,
                }}
                setValue={setValue}
                placeholder={"Search vehicle"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
              <ComboboxInput
                url={APIS.DRIVER}
                queryKey={"driver"}
                register={register("driver")}
                errors={errors?.driver}
                name={"driver"}
                label={"Driver"}
                setValue={setValue}
                props={{
                  is_active: true,
                }}
                placeholder={"Search driver"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
              <TextField
                label={"Origin"}
                register={register("from_branch")}
                errors={errors?.origin}
                disabled={true}
              />
              <ComboboxInput
                url={APIS.BRANCH}
                queryKey={"branch"}
                register={register("destination_branch")}
                errors={errors?.destination_branch}
                name={"destination_branch"}
                label={"Destination"}
                setValue={setValue}
                props={{
                  is_active: true,
                }}
                placeholder={"Search branch"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
            </div>
            <div className={tw`grid  grid-cols-4 gap-x-4 mx-4`}>
              <NumberField
                value={getValues("freight")}
                register={register("freight")}
                errors={errors?.freight}
                label={"Freight"}
                setValue={setValue}
                clearErrors={clearErrors}
              />
              <NumberField
                value={getValues("advance")}
                register={register("advance")}
                errors={errors?.advance}
                label={"Advance"}
                setValue={setValue}
                clearErrors={clearErrors}
              />
              <NumberField
                value={getValues("balance")}
                register={register("balance")}
                errors={errors?.balance}
                label={"Balance"}
                setValue={setValue}
                clearErrors={clearErrors}
              />
              {/* <MultipleSelectField
                label={"Allowed Cities"}
                queryKey={"allowed-city"}
                register={register("allowed_cities")}
                watch={watch}
                setValue={setValue}
                url={`${APIS.LOCATION}flat-location/`}
                errors={errors.allowed_cities}
                value={
                  data?.allowed_cities
                    ? data?.allowed_cities?.map((item: any) => ({
                        label: item?.name,
                        value: item?.id,
                      }))
                    : []
                }
                props={{
                  level: 3,
                  is_active: true,
                }}
              /> */}
            </div>
            <div className={tw`mx-4`}>
              <TextField
                label={"Note"}
                register={register(`note`, { disabled: !data?.is_active })}
                errors={errors?.note}
              />
            </div>
          </FormLayout>
        }
        allowedPermissions={["can_editupdate_manifest"]}
      />
      <Card title="Barcode Scanner">
        <>
          <div className={tw`flex flex-col  items-end w-full`}>
            <div
              className={tw`flex flex-row gap-x-4 items-center w-full justify-end`}
            >
              <ChangeStatus
                gridData={statusData}
                defaultValue={defaultValue}
                handleStatusChange={handleStatusChange}
              />
            </div>

            <div className={tw`grid grid-cols-1 w-full`}>
              <div>
                <input
                  type="text"
                  {...register("tracking_number")}
                  className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
                  onPaste={preventCopyPaste}
                  onKeyDown={handleKeyDown}
                />
                {errors?.tracking_number && (
                  <p className={tw`text-red-500 text-xs my-1`}>
                    {errors?.tracking_number?.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      </Card>
      {!data?.is_active ? (
        <DataTable
          url={`${APIS.VEHICLE_MANIFEST_ITEMS}`}
          queryKey={"manifest-items"}
          columns={manifestItemColumn}
          subOrderSearch={true}
          params={{ vehicle_manifest: data?.id, ordering: "is_received" }}
          tableName={"manifest-items-list"}
          handleDetailNavigate={handleOrderDetailNavigate}
          actionMenu={
            <div className={tw`w-full text-right flex  items-center`}>
              <button
                className={tw`flex gap-x-1 cursor-pointer mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                onClick={handleDownloadExcel}
              >
                <DownloadIcon color={"var(--primary)"} />
                Download
              </button>
              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                disabled={isPrintLoading}
                onClick={() => {
                  data?.is_active
                    ? toast.error("Please Mark as done first")
                    : handleSplitPrint();
                }}
              >
                {isPrintLoading ? <Spinner /> : "Split Print"}
              </button>
              {data?.is_active ? (
                <button
                  className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                  onClick={handleMarkAsDone}
                >
                  Mark As Done
                </button>
              ) : null}
              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                disabled={isPrintLoading}
                onClick={() => {
                  data?.is_active
                    ? toast.error("Please Mark as done first")
                    : handlePrint(getValues("transit_branch"));
                }}
              >
                {isPrintLoading ? <Spinner /> : "Print"}
              </button>
              {data?.is_active ? (
                <button
                  className={tw`p-1 px-5  rounded-sm border border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                  disabled={isRemoved}
                  onClick={() => handleRemove(setError)}
                >
                  {isRemoved ? <Spinner /> : "Remove"}
                </button>
              ) : (
                ""
              )}
            </div>
          }
        />
      ) : (
        <TabPanel
          tabTitle={["Booked Order", "Vehicle Manifest Items"]}
          tabPanels={[
            <>
              <OrderList cityIds={cityIds} />
            </>,
            <>
              <DataTable
                url={`${APIS.VEHICLE_MANIFEST_ITEMS}`}
                queryKey={"manifest-items"}
                columns={manifestItemColumn}
                subOrderSearch={true}
                params={{ vehicle_manifest: data?.id, ordering: "is_received" }}
                tableName={"manifest-items-list"}
                handleDetailNavigate={handleOrderDetailNavigate}
                actionMenu={
                  <div className={tw`w-full text-right flex  items-center`}>
                    <button
                      className={tw`flex gap-x-1 cursor-pointer mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                      onClick={handleDownloadExcel}
                    >
                      <DownloadIcon color={"var(--primary)"} />
                      Download
                    </button>
                    {!data?.is_active ? (
                      <button
                        className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                        disabled={isPrintLoading}
                        onClick={() => {
                          data?.is_active
                            ? toast.error("Please Mark as done first")
                            : handleSplitPrint();
                        }}
                      >
                        {isPrintLoading ? <Spinner /> : "Split Print"}
                      </button>
                    ) : null}

                    {data?.is_active ? (
                      <button
                        className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                        onClick={handleMarkAsDone}
                      >
                        Mark As Done
                      </button>
                    ) : null}
                    {!data?.is_active ? (
                      <button
                        className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                        disabled={isPrintLoading}
                        onClick={() => {
                          data?.is_active
                            ? toast.error("Please Mark as done first")
                            : handlePrint(getValues("transit_branch"));
                        }}
                      >
                        {isPrintLoading ? <Spinner /> : "Print"}
                      </button>
                    ) : null}
                    {data?.is_active ? (
                      <button
                        className={tw`p-1 px-5  rounded-sm border border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                        disabled={isRemoved}
                        onClick={() => handleRemove(setError)}
                      >
                        {isRemoved ? <Spinner /> : "Remove"}
                      </button>
                    ) : (
                      ""
                    )}
                  </div>
                }
              />
            </>,
          ]}
        />
      )}

      <Modal
        isOpen={showMarkAsDoneModal}
        setIsOpen={setShowMarkAsDoneModal}
        showTitle={false}
      >
        <div className={tw`grid place-items-center w-fit`}>
          <div className={tw`space-y-3`}>
            <h2 className={tw`text-xl font-bold text-center`}>Are you sure?</h2>
            <div className={tw`flex items-center gap-5 justify-end`}>
              <button
                className={tw`bg-gray-200 rounded-lg px-2 py-2 focus:outline-none`}
                onClick={handleMarkAsDoneModalClose}
              >
                Cancel
              </button>
              <button
                onClick={toggleMarkAsDone}
                className={tw`px-2 py-2 rounded-lg bg-primary  text-white disabled:bg-primary-light`}
                disabled={isMarkAsDoneLoading}
              >
                <p>Mark As done</p>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={openReviewModal}
        setIsOpen={setOpenReviewModal}
        title={"Remark"}
      >
        <Remark selectedOrder={selectedOrder} />
      </Modal>
    </>
  );
};

export default EditVehicleManifest;
